import React, { useCallback, useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { useTranslate } from 'gatsby-theme-soluble-source';

import CarouselSection, {
    CarouselSectionProps,
} from 'design-system/src/components/organisms/CarouselSection';
import { formatResource } from '../../utils/resources';

interface FilterResourceBlock {
    name: string;
    kind: string;
}

interface JoinCommunitySectionProps {
    title: string;
    titleTag: CarouselSectionProps['titleTag'];
    filters: FilterResourceBlock[];
    events: any[];
    webinars: any[];
    ebooks: any[];
    articles: any[];
}

export default function JoinCommunitySection({
    title,
    titleTag,
    filters,
    events,
    webinars,
    ebooks,
    articles,
}: JoinCommunitySectionProps) {
    const _ = useTranslate();

    const [selectedFilter, setSelectedFilter] = useState<string | null>(
        filters?.[0]?.kind ?? null,
    );

    const thisFilters = useMemo(() => {
        return filters?.length > 0
            ? filters.map((filter: FilterResourceBlock) => ({
                  label: filter.name,
                  value: filter.kind,
              }))
            : [];
    }, [filters]);

    const thisItems = useMemo(() => {
        let allItems = [...events, ...webinars, ...ebooks, ...articles];

        if (!allItems || !allItems.length) {
            return [];
        }

        const filterByTypename: Record<string, string> = {
            DatoCmsEbook: 'ebooks',
            DatoCmsEvent: 'events',
            DatoCmsWebinar: 'webinars',
            DatoCmsBlogPost: 'articles',
            DatoCmsGlossaryTerm: 'articles',
        };

        const tagByTypename: Record<string, string> = {
            DatoCmsEbook: 'Ebook',
            DatoCmsEvent: 'Event',
            DatoCmsWebinar: 'Webinar',
            DatoCmsBlogPost: 'Article',
            DatoCmsGlossaryTerm: 'Article',
        };

        allItems = allItems.map((item) => ({
            ...formatResource(item, item.typename, _),
            filter: filterByTypename[item.typename],
            tag: tagByTypename[item.typename],
        }));

        return allItems.filter((item) => {
            if (!selectedFilter) {
                return true;
            }

            return item.filter === selectedFilter;
        });
    }, [events, webinars, ebooks, articles, _, selectedFilter]);

    const onClickFilter = useCallback((tag: string) => {
        setSelectedFilter((prevFilter) => {
            return prevFilter === tag ? null : tag;
        });
    }, []);

    return (
        <CarouselSection
            variant="mediaLarge"
            titleTag={titleTag}
            title={title}
            filters={thisFilters}
            onClickFilter={onClickFilter}
            items={thisItems}
            filterActive={selectedFilter}
        />
    );
}

export const query = graphql`
    fragment DatoCmsFilterResourceBlock on DatoCmsFilterResourceBlock {
        name
        kind
    }
    fragment DatoCmsJoinCommunitySection on DatoCmsJoinCommunitySection {
        title
        titleTag
        filters {
            ...DatoCmsFilterResourceBlock
        }
        events {
            id
            typename: __typename
            ...EventItem
        }
        webinars {
            id
            typename: __typename
            ...WebinarItem
        }
        ebooks {
            id
            typename: __typename
            ...EbookItem
        }
        articles {
            ... on DatoCmsBlogPost {
                id
                typename: __typename
                href: slug
                ...DatoCmsBlogPostItem
            }
            ... on DatoCmsGlossaryTerm {
                id
                typename: __typename
                text: summary
                href: slug
                ...DatoCmsGlossaryTermItem
            }
        }
    }
`;
